import type { StorageAdapter } from '../storage/types';
import type { SchemaAdapter } from '../schema/adapter';
import {
  createUser,
  isUser,
  userFcid,
  USER_FCID_PREFIX,
  type User,
  type CreateUserInput,
  type UserMembership,
  type UserRole,
} from '../../types/models/User';

/**
 * F 290 — THE USER STORE. `AppStore` with the tier relationship removed.
 *
 * THE CLAIM THIS FILE MAKES, AND THE TEST THAT GUARDS IT: the user path required
 * **no adapter change**, exactly as the project and app paths did not.
 * `StorageAdapter` is key/value, the kind lives in the key, and every adapter
 * already implements `list(prefix)`. A user therefore lives at `user:<id>` and
 * `QueryLayer.list('user')` finds it with no change to the query layer. A test
 * asserts this store's source names no adapter, so a future kind-specific branch
 * fails rather than hiding.
 *
 * The key convention is the query layer's own, `KEY_PREFIX(kind) = kind + ':'` at
 * `src/core/query/query-layer.ts:193`. **Third kind to generalise** — F 275
 * (`project`), F 276 (`app`), F 280 (`view`), F 290 (`user`).
 *
 * WHERE THIS STORE DIFFERS FROM AppStore, AND WHY IT IS NOT A COPY. `AppStore`
 * filters and scopes on a single required `project: string`. A user belongs to
 * many projects, so there is nothing single to scope on. The reads here filter on
 * `memberships` instead, and the reverse direction — a project's users — is
 * DERIVED from the same list rather than stored a second time on the project. One
 * source of truth, read two ways; a user who joins a project is edited in exactly
 * one place, and no record of that membership exists anywhere else to disagree.
 */

/** Matches the query layer's `KEY_PREFIX` convention. One source of truth for the shape. */
export const USER_KEY_PREFIX = 'user:';

export type UserResult =
  | { ok: true; user: User; created: boolean }
  | {
      ok: false;
      code: 'empty-name' | 'not-found' | 'duplicate' | 'invalid-record' | 'no-such-project';
      message: string;
    };

export interface CreateUserRecordInput extends CreateUserInput {
  /** Refuse to overwrite an existing user with this id. Default true. */
  failIfExists?: boolean;
}

export interface UpdateUserInput {
  name?: string | null;
  email?: string | null;
  label?: string | null;
  description?: string | null;
  /** Replaces the whole membership list. `id` and `fcid` are never patchable. */
  memberships?: UserMembership[] | null;
  order?: number | null;
  now?: string;
}

export interface ListUsersInput {
  /** Only users who are a member of this project. Omit for all. */
  project?: string;
  /** Only users with this role in `project`. Ignored without `project`. */
  role?: UserRole;
  /** Omit to include soft-deleted rows. F 226 semantics. */
  includeDeleted?: boolean;
  limit?: number;
  offset?: number;
}

export interface AddMembershipInput {
  project: string;
  role: UserRole;
  /** Overwrite the role if the user is already a member. Default false. */
  replace?: boolean;
  now?: string;
}

export interface UserStoreOptions {
  /**
   * The storage adapter. THE ONLY persistence path, exactly as in F 275 and
   * F 276 — which is what makes "persists through every adapter" structural
   * rather than four parallel implementations.
   */
  storage?: StorageAdapter;
  /** Used only for `userCollectionDecl()` / `registerUserCollection()`. */
  schema?: SchemaAdapter;
  /** Seed rows, applied on construction. */
  initial?: User[];
}

export class UserStore {
  private readonly users = new Map<string, User>();

  constructor(private readonly opts: UserStoreOptions = {}) {
    for (const u of opts.initial ?? []) this.users.set(u.id, u);
  }

  // ── reads ──────────────────────────────

  /** One user by id, or null. */
  getUser(id: string): User | null {
    return this.users.get(id) ?? null;
  }

  /** One user by id INCLUDING soft-deleted rows. The Trash read. */
  getUserIncludingDeleted(id: string): User | null {
    return this.users.get(id) ?? null;
  }

  /**
   * ITEM 4(a)/4(e) — the projects a user belongs to.
   *
   * Returns project IDS, not Project records. This store has no knowledge of
   * ProjectStore, and importing one would be exactly the cross-kind coupling
   * `AppStore.projectOfApp` refuses. The id is the contract; whoever holds the
   * project resolves it against their own store.
   */
  projectsOfUser(id: string): string[] {
    const u = this.users.get(id);
    if (!u) return [];
    return (u.memberships ?? []).map((m) => m.project);
  }

  /**
   * ITEM 4(b)/4(f) — a project's users. DERIVED from the users' own memberships.
   *
   * There is deliberately no `members` list on a project to disagree with this.
   */
  listUsersOfProject(project: string, input: Omit<ListUsersInput, 'project'> = {}): User[] {
    return this.listUsers({ ...input, project });
  }

  /** The role a user holds in a project, or null. */
  roleOfUserInProject(userId: string, project: string): UserRole | null {
    const u = this.users.get(userId);
    if (!u) return null;
    return (u.memberships ?? []).find((m) => m.project === project)?.role ?? null;
  }

  /** All live users, ordered by (name, id) so the list is stable. */
  allUsers(): User[] {
    return this.sorted(this.users.values()).filter((u) => !u.deletedAt);
  }

  listUsers(input: ListUsersInput = {}): User[] {
    let rows = [...this.users.values()];
    if (!input.includeDeleted) rows = rows.filter((u) => !u.deletedAt);
    if (input.project !== undefined) {
      rows = rows.filter((u) => (u.memberships ?? []).some((m) => m.project === input.project));
    }
    if (input.role !== undefined) {
      if (input.project === undefined) {
        throw new Error('listUsers: `role` filters a role WITHIN a project; pass `project` too.');
      }
      rows = rows.filter((u) =>
        (u.memberships ?? []).some((m) => m.project === input.project && m.role === input.role),
      );
    }
    rows = this.sorted(rows);
    if (input.offset !== undefined) rows = rows.slice(input.offset);
    if (input.limit !== undefined) rows = rows.slice(0, input.limit);
    return rows;
  }

  private sorted(rows: Iterable<User>): User[] {
    return [...rows].sort((a, b) => {
      if (a.name !== b.name) return a.name < b.name ? -1 : 1;
      return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
    });
  }

  // ── writes ──────────────────────────────

  /**
   * Create and persist a user.
   *
   * Writes to `storage` BEFORE updating the map, so a store that fails to write
   * does not report a user it could not persist. Same order, same reason, as
   * `AppStore.createAppRecord`.
   */
  async createUserRecord(input: CreateUserRecordInput): Promise<UserResult> {
    const name = typeof input.name === 'string' ? input.name.trim() : '';
    if (!name) {
      return { ok: false, code: 'empty-name', message: 'A user needs a non-empty name.' };
    }
    const user = createUser({ ...input, name });
    const existing = this.users.get(user.id);
    if (existing && input.failIfExists !== false) {
      return {
        ok: false,
        code: 'duplicate',
        message: `A user with id "${user.id}" already exists.`,
      };
    }
    if (this.opts.storage) {
      try {
        await this.opts.storage.save(`${USER_KEY_PREFIX}${user.id}`, user);
      } catch (e) {
        return {
          ok: false,
          code: 'invalid-record',
          message: `Could not persist user "${user.id}": ${(e as Error)?.message ?? e}`,
        };
      }
    }
    this.users.set(user.id, user);
    return { ok: true, user, created: !existing };
  }

  /**
   * Patch a user. `id` and `fcid` are NOT patchable.
   *
   * `memberships` REPLACES the whole list rather than merging, and a caller that
   * wants to add one should use `addMembership`. A field-level merge on a list of
   * records with a `role` in them is the kind of quiet thing that turns "joined
   * two projects" into "one project with the last role", and a membership that
   * silently disappears is worse than one that must be written deliberately.
   */
  async updateUser(id: string, input: UpdateUserInput): Promise<UserResult> {
    const current = this.users.get(id);
    if (!current) {
      return { ok: false, code: 'not-found', message: `No user with id "${id}".` };
    }
    if (input.name !== undefined && !input.name?.trim()) {
      return { ok: false, code: 'empty-name', message: 'A user needs a non-empty name.' };
    }
    const next: User = {
      ...current,
      name: input.name === undefined ? current.name : input.name ?? current.name,
      email: input.email === undefined ? current.email : input.email ?? undefined,
      label: input.label === undefined ? current.label : input.label ?? undefined,
      description:
        input.description === undefined ? current.description : input.description ?? undefined,
      memberships:
        input.memberships === undefined
          ? current.memberships
          : input.memberships === null
            ? undefined
            : input.memberships,
      order: input.order === undefined ? current.order : input.order,
      updatedAt: input.now ?? new Date().toISOString(),
    };
    if (this.opts.storage) {
      try {
        await this.opts.storage.save(`${USER_KEY_PREFIX}${id}`, next);
      } catch (e) {
        return {
          ok: false,
          code: 'invalid-record',
          message: `Could not persist user "${id}": ${(e as Error)?.message ?? e}`,
        };
      }
    }
    this.users.set(id, next);
    return { ok: true, user: next, created: false };
  }

  /**
   * ITEM 4(a) — join a project. The write side of the relationship.
   *
   * Refuses a duplicate by default so a double-click cannot create two rows for
   * one project; `replace: true` turns the role instead.
   */
  async addMembership(id: string, input: AddMembershipInput): Promise<UserResult> {
    const current = this.users.get(id);
    if (!current) {
      return { ok: false, code: 'not-found', message: `No user with id "${id}".` };
    }
    const project = input.project?.trim();
    if (!project) {
      return { ok: false, code: 'no-such-project', message: 'addMembership: project is required.' };
    }
    const list = [...(current.memberships ?? [])];
    const at = list.findIndex((m) => m.project === project);
    if (at >= 0) {
      if (!input.replace) {
        return {
          ok: false,
          code: 'duplicate',
          message: `"${id}" is already a member of "${project}" (role "${list[at].role}").`,
        };
      }
      list[at] = { project, role: input.role };
    } else {
      list.push({ project, role: input.role });
    }
    return this.updateUser(id, { memberships: list, now: input.now });
  }

  /** Leave a project. The other side of `addMembership`. */
  async removeMembership(id: string, project: string, now?: string): Promise<UserResult> {
    const current = this.users.get(id);
    if (!current) {
      return { ok: false, code: 'not-found', message: `No user with id "${id}".` };
    }
    const list = (current.memberships ?? []).filter((m) => m.project !== project);
    return this.updateUser(id, { memberships: list, now });
  }

  /** Hard delete. Removes the row from memory and from storage. */
  async deleteUser(id: string): Promise<UserResult> {
    const current = this.users.get(id);
    if (!current) {
      return { ok: false, code: 'not-found', message: `No user with id "${id}".` };
    }
    if (this.opts.storage) {
      await this.opts.storage.delete(`${USER_KEY_PREFIX}${id}`);
    }
    this.users.delete(id);
    return { ok: true, user: current, created: false };
  }

  /** F 226 soft delete. The row stays; default reads exclude it. */
  async softDeleteUser(id: string, now?: string): Promise<UserResult> {
    const current = this.users.get(id);
    if (!current) {
      return { ok: false, code: 'not-found', message: `No user with id "${id}".` };
    }
    const ts = now ?? new Date().toISOString();
    const next: User = { ...current, deletedAt: ts, updatedAt: ts };
    if (this.opts.storage) {
      await this.opts.storage.save(`${USER_KEY_PREFIX}${id}`, next);
    }
    this.users.set(id, next);
    return { ok: true, user: next, created: false };
  }

  /** F 226 restore. Clears `deletedAt` and returns the row to default reads. */
  async restoreUser(id: string, now?: string): Promise<UserResult> {
    const current = this.users.get(id);
    if (!current) {
      return { ok: false, code: 'not-found', message: `No user with id "${id}".` };
    }
    const { deletedAt: _drop, ...live } = current;
    const next: User = { ...live, updatedAt: now ?? new Date().toISOString() };
    if (this.opts.storage) {
      await this.opts.storage.save(`${USER_KEY_PREFIX}${id}`, next);
    }
    this.users.set(id, next);
    return { ok: true, user: next, created: false };
  }

  // ── rehydration ────────────────────────────

  /**
   * Load every `user:*` row out of storage. Without this the store is a memory
   * cache that forgets on reload. Same traversal the query layer performs at
   * `query-layer.ts:233` (`adapter.list(KEY_PREFIX(kind))`).
   */
  async hydrate(): Promise<number> {
    if (!this.opts.storage) return 0;
    const keys = await this.opts.storage.list(USER_KEY_PREFIX);
    let loaded = 0;
    for (const key of keys) {
      if (!key.startsWith(USER_KEY_PREFIX)) continue;
      const value = await this.opts.storage.load(key);
      if (!isUser(value)) continue;
      this.users.set(value.id, value);
      loaded += 1;
    }
    return loaded;
  }

  /** The stored key for a user id. Exported for adapters and tests. */
  static keyFor(id: string): string {
    return `${USER_KEY_PREFIX}${id}`;
  }

  // ── schema declaration ──────────────────────────

  /**
   * The `user` collection, declared ONCE — returned, not applied, exactly as
   * `AppStore.appCollectionDecl()` does.
   *
   * `name` is REQUIRED. `email` is NOT, deliberately, and the asymmetry is the
   * design: §47.2 requires a name and says nothing about an email, so a
   * required column here would let the write side invent a constraint the
   * doctrine never stated. See `User.email`.
   */
  userCollectionDecl() {
    return {
      name: 'user',
      fields: [
        { name: 'name', type: 'text' as const, required: true },
        { name: 'email', type: 'text' as const },
        { name: 'label', type: 'text' as const },
        { name: 'description', type: 'text' as const },
        { name: 'fcid', type: 'text' as const },
        { name: 'parentId', type: 'text' as const },
        { name: 'order', type: 'number' as const },
        { name: 'edge', type: 'text' as const },
        { name: 'deletedAt', type: 'date' as const },
        { name: 'createdAt', type: 'date' as const },
        { name: 'updatedAt', type: 'date' as const },
      ],
      indexes: ['name', 'email'],
      // Default-deny, exactly like every other collection (F 156).
      permissions: {
        list: 'auth', view: 'auth', create: 'operator', update: 'operator', delete: 'operator',
      },
    };
  }

  /** Convenience: register `user` through an F 156 SchemaAdapter. */
  async registerUserCollection() {
    if (!this.opts.schema) {
      throw new Error('UserStore has no SchemaAdapter; register the collection from the host.');
    }
    return this.opts.schema.emitSchemaMigration(this.userCollectionDecl() as any, {});
  }
}

export { USER_FCID_PREFIX, userFcid, isUser };
export type { User };
